import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, useParams, useNavigate } from "react-router-dom";

import SettingsPage from '../partials/SettingsPage';
import LoadingSpinner from '../partials/LoadingSpinner';
import PageHeader from '../partials/PageHeader';

import AddSupplierAssignmentModal from './AddSupplierAssignmentModal';
import UpdateSupplierAssignmentModal from './UpdateSupplierAssignmentModal';
import DeleteSupplierAssignmentModal from './DeleteSupplierAssignmentModal';

import { Button, Text, Small, Panel, H3, H4, Dropdown, Input, Tabs, Datepicker, Textarea, Box, Select } from '@bigcommerce/big-design';
import { AddIcon, ArrowDropDownIcon } from '@bigcommerce/big-design-icons';

import { purchaseOrdersCreate, purchaseOrderAddItem, purchaseOrderUpdateQuantity } from '../../redux/actions/purchaseOrders';
import { reset } from '../../redux/actions/purchaseOrderLines';
import { suppliersIndex } from '../../redux/actions/suppliers';
import { taxSettingsIndex } from '../../redux/actions/taxSettings';
import { paymentTermsIndex } from '../../redux/actions/paymentTerms';
import { paymentMethodsIndex } from '../../redux/actions/paymentMethods';
import { locationsIndex } from '../../redux/actions/locations';
import { orderWithShipments } from '../../redux/actions/incompleteOrders';

import AssignmentList from './AssignmentList';
import ProductSearch from './ProductSearch';
import ProductScan from './ProductScan';
import PurchaseOrderLine from './PurchaseOrderLine';
import PurchaseOrderTotals from './PurchaseOrderTotals';

const NewDropshipPurchaseOrderForm = ({ 
  payload,
  newPurchaseOrder,
  taxSettings,
  paymentTerms,
  paymentMethods,
  suppliers,
  products,
  loadTaxSettings,
  loadPaymentTerms,
  loadPaymentMethods,
  loadSuppliers,
  locations,
  loadOrder,
  addPurchaseOrder,
  purchaseOrderLines,
  activeOrder,
  incompleteOrders,
  clearPOLines,
  addItem,
  quantityUpdate,
  settings
}) => {
  const { orderId } = useParams();

  let navigate = useNavigate();

  const [activeSupplier, setActiveSupplier] = useState(null);
  const [activeShippingAddress, setActiveShippingAddress] = useState(null);
  const [activePaymentTerm, setActivePaymentTerm] = useState(null);
  const [activePaymentMethod, setActivePaymentMethod] = useState(null);
  const [activeTaxSetting, setActiveTaxSetting] = useState(null);

  const [invoiceNumber, setInvoiceNumber] = useState(null);
  const [supplierOrderNumber, setSupplierOrderNumber] = useState(null);
  const [notes, setNotes] = useState(null);

  const [activeTab, setActiveTab] = useState('list');

  const [adjustedTotal, setAdjustedTotal] = useState(null);
  const [shippingTotal, setShippingTotal] = useState(null);
  const [expectedDate, setExpectedDate] = useState(null);
  const [paymentDate, setPaymentDate] = useState(null);

  const [submitted, setSubmitted] = useState(false);

  const [showAddSupplierAssignmentModal, setShowAddSupplierAssignmentModal] = useState(false);
  const [modalProductId, setModalProductId] = useState(null);
  const [modalVariantId, setModalVariantId] = useState(null);
  const [modalOptions, setModalOptions] = useState(null);
  const [modalProductName, setModalProductName] = useState(null);
  const [modalType, setModalType] = useState(null);
  const [modalAssignment, setModalAssignment] = useState(null);

  const [warnings, setWarnings] = useState({ moq: {}, orderMultiple: {} });

  const setOrderLineMatches = () => {
    clearPOLines();

    if(activeOrder) {
      let matchingAssignment = false;
      // let matchingAssignmentSettings = {};

      activeOrder.order.line_items.forEach(lineItem => {
        let matchingAssignmentSettings = {};

        if(activeSupplier) {
          matchingAssignment = products?.searchedVariantsMetafields[`${lineItem.product_id}/${lineItem.variant_id}`]?.data?.find(assignment => assignment?.key === `Supplier-${activeSupplier.id}`);
          matchingAssignment ||= activeOrder.variant_metafields[`${lineItem.product_id}/${lineItem.variant_id}`]?.data?.find(assignment => assignment?.key === `Supplier-${activeSupplier.id}`);

          if(matchingAssignment) {
            matchingAssignmentSettings = JSON.parse(matchingAssignment.value)
          }
        }

        let goodMatch = !!matchingAssignment;

        if(settings?.settings?.pack_quantities) {
          goodMatch = goodMatch && !(parseInt(matchingAssignmentSettings.pack_quantity) > 1);
        }

        let lineObject = {
          imageUrl: lineItem.image_url,
          fullImageUrl: lineItem.full_image_url,
          minimumOrder: matchingAssignmentSettings?.minimum_order,
          options: lineItem.options,
          productId: lineItem.product_id,
          productName: lineItem.name,
          name: lineItem.name,
          sku: lineItem.sku,
          supplier_price: matchingAssignmentSettings?.supplier_price || 0.0,
          supplier_sku: matchingAssignmentSettings?.supplier_sku,
          upc: lineItem.upc,
          variantId: lineItem.variant_id,
          orderMatchingAssignment: goodMatch
        }

        if(settings?.settings?.pack_quantities) {
          lineObject.packQuantity = matchingAssignmentSettings.pack_quantity;
        }

        addItem(payload, lineObject)

        quantityUpdate(payload, lineItem.quantity, lineItem.product_id + '/' + lineItem.variant_id)
      });
    }
  };

  useEffect(() => {
    clearPOLines();

    loadTaxSettings(payload);
    loadPaymentTerms(payload);
    loadPaymentMethods(payload);
    loadSuppliers(payload);

    loadOrder(payload, orderId);
  }, []);

  useEffect(() => {
    let newWarnings = { moq: {}, orderMultiple: {} };

    purchaseOrderLines.lineItems.forEach(lineItem => {
      let itemKey = lineItem.productId + '/' + lineItem.variantId;

      if(parseInt(lineItem.minimumOrder) > purchaseOrderLines.lineDetails[itemKey].quantity) {
        newWarnings.moq[itemKey] = `${lineItem.name || lineItem.productName} line quantity below MOQ: ${lineItem.minimumOrder} vs. ${purchaseOrderLines.lineDetails[itemKey].quantity}`
      }

      if(settings?.settings?.order_multiples) {
        if(lineItem.orderMultiple && ((purchaseOrderLines.lineDetails[itemKey].quantity % lineItem.orderMultiple) !== 0)) {
          newWarnings.orderMultiple[lineItem.productId + '/' + lineItem.variantId] = `${lineItem.name || lineItem.productName} line not divisible by assignment order multiple: ${lineItem.orderMultiple}`
        }
      }
    });

    setWarnings(newWarnings);
  }, [purchaseOrderLines]);

  useEffect(() => {
    if(activeOrder) {
      if(activeOrder.shipments) {
        let shipment = activeOrder.shipments[0];

        setActiveShippingAddress({
          label: 'Customer Address',
          company: shipment.company,
          first_name: shipment.first_name,
          last_name: shipment.last_name,
          street_1: shipment.street_1,
          street_2: shipment.street_2,
          city: shipment.city,
          state: shipment.state,
          zip: shipment.zip,
        });
      }

      setOrderLineMatches();
    
      // set the invoice number to the order number
      setInvoiceNumber(`Order ${activeOrder.order.order_id}`);

    }
  }, [activeOrder])

  useEffect(() => {
    setOrderLineMatches();
  }, [activeSupplier]);

  useEffect(() => {
    setOrderLineMatches();
  }, [products]);


  useEffect(() => {
    if(newPurchaseOrder) { navigate(-1) };
  }, [newPurchaseOrder]);

  const purchaseOrderJson = () => {
    return {
      supplier: activeSupplier,
      shipping_address: activeShippingAddress,
      payment_term: activePaymentTerm,
      payment_method: activePaymentMethod,
      invoice_number: invoiceNumber,
      tax_setting: activeTaxSetting,
      supplier_order_number: supplierOrderNumber,
      notes: notes,
      invoice_date: invoiceDate,
      adjusted_total: adjustedTotal,
      shipping_total: shippingTotal || 0.0,
      expected_date: expectedDate,
      payment_due_date: paymentDate
    }
  }

  const purchaseOrderLinesJson = () => {
    let orderLines = [];

    purchaseOrderLines?.lineItems?.forEach(purchaseOrderLine => {
      let key = purchaseOrderLine.productId + '/' + purchaseOrderLine.variantId;
      let lineWithDetails = {...purchaseOrderLine};
      lineWithDetails.line_details = purchaseOrderLines.lineDetails[key];
      orderLines.push(lineWithDetails);
    })

    // console.log(orderLines);

    return orderLines;
  }

  const setFieldValue = (setField, event) => {
    let value = event.target.value;
    setField(value);
  }

  const setDropdownValue = (setField, value) => {
    setField(value);
  }

  const activateSupplier = (supplier) => {
    setActiveSupplier(supplier);
    setActivePaymentTerm(paymentTerms.paymentTerms.find(term => term.id === supplier.payment_terms_id));
    setActivePaymentMethod(paymentMethods.paymentMethods.find(method => method.id === supplier.payment_method_id));
    setActiveTaxSetting(taxSettings.taxSettings.find(setting => setting.id === supplier.tax_setting_id));

    if(supplier.shipping_days) {
      let expected = new Date();

      expected.setDate(expected.getDate() + parseInt(supplier.shipping_days));

      const yyyy = expected.getFullYear();
      let mm = expected.getMonth() + 1; // months start at 0
      let dd = expected.getDate();

      if (dd < 10) dd = '0' + dd;
      if (mm < 10) mm = '0' + mm;

      let formattedExpected = yyyy + '-' + mm + '-' + dd + 'T05:00:00.000Z';


      setExpectedDate(formattedExpected);
    }
  }

  const supplierSelectItems = () => {
    let items = [];

    suppliers.suppliers.map(supplier => 
      items.push(
        {
          value: supplier,
          content: supplier.supplier_name
        }
      )
    )

    return items;
  }


  const paymentTermsItems = () => {
    let items = [];

    paymentTerms.paymentTerms.map(paymentTerm => 
      items.push(
        {
          content: paymentTerm.term_name,
          onItemClick: () => setDropdownValue(setActivePaymentTerm, paymentTerm),
          hash: paymentTerm.id
        }
      )
    )

    return items;
  }

  const paymentMethodsItems = () => {
    let items = [];

    paymentMethods.paymentMethods.map(paymentMethod => 
      items.push(
        {
          content: paymentMethod.method_name,
          onItemClick: () => setDropdownValue(setActivePaymentMethod, paymentMethod),
          hash: paymentMethod.id
        }
      )
    )

    return items;
  }

  const taxSettingsItems = () => {
    let items = [];

    taxSettings.taxSettings.map(taxSetting => 
      items.push(
        {
          content: taxSetting.tax_name,
          onItemClick: () => setDropdownValue(setActiveTaxSetting, taxSetting),
          hash: taxSetting.id
        }
      )
    )

    return items;
  }

  const handleSubmit = () => {
    setSubmitted(true);
    addPurchaseOrder(payload, purchaseOrderJson(), purchaseOrderLinesJson());
  }

  const validPO = () => {
    let block = !(activeShippingAddress && activePaymentTerm && activePaymentMethod && activeTaxSetting && expectedDate && paymentDate && invoiceDate && (purchaseOrderLines?.lineItems.length > 0))

    if(settings?.settings?.warnings_block) {
      if (Object.keys(warnings.moq).length > 0 || Object.keys(warnings.orderMultiple).length > 0) {
        block = true;
      }
    }

    return block;
  }

  const description = () => {
    return(
      <> 
        <Text>From this page, you can create purchase orders.</Text>
        <Text>Start by selecting a supplier to send this purchase order to. If the supplier you're looking for is missing, you can add it by going to the <Link to="/suppliers">Suppliers page</Link>.</Text>
        <Text>
          Select an address for the order to be sent to from the "Ship To Address" dropdown menu. If the address you're shipping to is missing, you can add it by going to the <Link to="/locations">Addresses page</Link>.
          If the address selected is a BigCommerce location, product inventory levels will be updated when receiving orders. Otherwise, inventory levels will not be modified when receiving this order.
        </Text>
        <Text>Set payment terms, a payment method and tax settings from their respective dropdowns. If options are missing, you can add them by going to the <Link to="/settings">Settings page</Link>.</Text>
        <Text>
          Add product/variant lines to this purchase order by selecting from a paginated set of products assigned to the selected supplier in the "List Supplier Assignments" tab, by searching BigCommerce in the "Search BigCommerce" tab, or by scanning barcodes in the "Scan Barcodes" tab.
          If products are missing from this supplier, you can add them by going to the <Link to="/assignments">Supplier Assignments page</Link>.
        </Text>
        <Text>Add additional information and notes in the "Totals, Adjustments and Dates" panel, then create the purchase order by clicking the "Create Purchase Order" button.</Text>
        <Small>Have questions or need some help? Contact us at <a href="mailto:support@athousandapps.com">support@athousandapps.com</a></Small>
      </>
    )
  }

  const today = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    let mm = today.getMonth() + 1; // months start at 0
    let dd = today.getDate();

    if (dd < 10) dd = '0' + dd;
    if (mm < 10) mm = '0' + mm;

    let todayDate = yyyy + '-' + mm + '-' + dd + 'T05:00:00.000Z';

    return todayDate;
  }

  const [invoiceDate, setInvoiceDate] = useState(today());

  const setInvoiceD = (value) => {
    setInvoiceDate(value);
  }

  const nextYear = () => {
    const today = new Date();
    const yyyy = today.getFullYear() + 1;
    let mm = today.getMonth() + 1; // months start at 0
    let dd = today.getDate();

    if (dd < 10) dd = '0' + dd;
    if (mm < 10) mm = '0' + mm;

    let next = mm + '/' + dd + '/' + yyyy;

    return next;
  }

  return(
    <>
      <PageHeader stripeBackground='#c66628' helpLink="bigcommerce/create-and-update-purchase-orders/" />
      <SettingsPage title="Purchase Orders" description={description()}>
        <Panel>
          <H3>New Purchase Order</H3>

          <Panel>
            <div className="flex-container">
              <div className="flex-1">
                { (suppliers.isFetching || paymentTerms.status !== 'success' || paymentMethods.status !== 'success' || taxSettings.status !== 'success') &&
                  <LoadingSpinner statusText="Loading Suppliers" />
                }

                { suppliers.status === 'success' && paymentTerms.status === 'success' && paymentMethods.status === 'success' && taxSettings.status === 'success' && suppliers.suppliers.length > 0 &&
                  <>
                    <H3>Suppliers</H3>
                    <Select
                      filterable={true}
                      label="Suppliers"
                      maxHeight={300}
                      onOptionChange={(supplier) => activateSupplier(supplier)}
                      options={supplierSelectItems()}
                      placeholder="Select Supplier"
                      placement="bottom-start"
                      required
                      value={activeSupplier?.supplier_name}
                    />
                  </>
                }

                { suppliers.suppliers.length === 0 && !suppliers.isFetching &&
                  <Box
                    backgroundColor="warning10"
                    border="box"
                    borderRadius="normal"
                    padding="medium"
                  >
                    <div className="text-center">
                      <Text>No suppliers available. Add supplier information to see suppliers in a dropdown list here.</Text>
                      <Link to="/suppliers">
                        <Button iconLeft={<AddIcon />}>
                          Manage Suppliers
                        </Button>
                      </Link>
                    </div>
                  </Box>
                }

                { activeSupplier &&
                  <>
                    <H3>Supplier: { activeSupplier.supplier_name }</H3>
                    <H4>Address</H4>
                    <Text>{activeSupplier.address.company ? `${activeSupplier.address.company} - ` : ''}{activeSupplier.address.first_name} {activeSupplier.address.last_name}
                    <br/>
                    {activeSupplier.address.street_1}
                    { activeSupplier.address.street_2 &&
                      <>
                        <br/>
                        {activeSupplier.address.street_2}
                      </>
                    }
                    <br/>
                    {activeSupplier.address.city}, {activeSupplier.address.state}
                    <br/>
                    {activeSupplier.address.zip}</Text>
                  </>
                }
              </div>
              
              <div className="flex-1 text-right left-15">
                { activeShippingAddress && 
                  <>
                    <H3>Ship To Address: { activeShippingAddress?.label }</H3>
                    <H4>Address</H4>
                    <Text>{activeShippingAddress.company ? `${activeShippingAddress.company} - ` : ''}{activeShippingAddress.first_name} {activeShippingAddress.last_name}
                    <br/>
                    {activeShippingAddress.street_1 || activeShippingAddress?.address?.address1}
                    { (activeShippingAddress.street_2 || activeShippingAddress?.address?.address2) &&
                      <>
                        <br/>
                        {activeShippingAddress.street_2 || activeShippingAddress?.address?.address2}
                      </>
                    }
                    <br/>
                    {activeShippingAddress.city || activeShippingAddress?.address?.city}, {activeShippingAddress.state || activeShippingAddress?.address?.state}
                    <br/>
                    {activeShippingAddress.zip || activeShippingAddress?.address?.zip}</Text>
                  </>
                }
              </div>
            </div>
        </Panel>

          <Panel>
            <H3>Purchase Order Information</H3>

            <div className="flex-container space-between">
              { paymentTerms.status === 'success' &&
                <div className="flex-1">
                  <H4>Payment Terms</H4>
                  <Dropdown
                    items={paymentTermsItems()}
                    maxHeight={250}
                    placement="bottom-start"
                    toggle={<Button iconRight={<ArrowDropDownIcon />}>{activePaymentTerm?.term_name || "Payment Terms"}</Button>}
                  />
                </div>
              }

              { paymentMethods.status === 'success' &&
                <div className="flex-1">
                  <H4>Payment Method</H4>
                  <Dropdown
                    items={paymentMethodsItems()}
                    maxHeight={250}
                    placement="bottom-start"
                    toggle={<Button iconRight={<ArrowDropDownIcon />}>{activePaymentMethod?.method_name || "Payment Method"}</Button>}
                  />
                </div>
              }

              { taxSettings.status === 'success' &&
                <div className="flex-1">
                  <H4>Tax Settings</H4>
                  <Dropdown
                    items={taxSettingsItems()}
                    maxHeight={250}
                    placement="bottom-start"
                    toggle={<Button iconRight={<ArrowDropDownIcon />}>{activeTaxSetting?.tax_name || "Tax Setting"}</Button>}
                  />  
                </div>
              }
            </div>

            <hr/>

            <Input 
              required 
              label={`${settings.settings['invoice_number_override'] || "Invoice Number"}`}
              placeholder={`${settings.settings['invoice_number_override'] || "Invoice Number"}`}
              id="invoice_number" 
              type="text" 
              value={invoiceNumber} 
              onChange={(e) => setFieldValue(setInvoiceNumber, e)} 
            />

            <Input 
              required 
              label={`${settings.settings['purchase_order_number_override'] || "Purchase Order Number"}`}
              placeholder={`${settings.settings['purchase_order_number_override'] || "Purchase Order Number"}`}
              id="supplier_order_number" 
              type="text" 
              value={supplierOrderNumber} 
              onChange={(e) => setFieldValue(setSupplierOrderNumber, e)} 
            />
          </Panel>
          

          <Panel>
            <H3>Order Lines</H3>
            { purchaseOrderLines?.lineItems?.map(purchaseOrderLine => 
              <Box
                backgroundColor={ purchaseOrderLine.orderMatchingAssignment ? "success10" : "danger10" }
              >
                <PurchaseOrderLine
                  purchaseOrderLine={purchaseOrderLine}
                  purchaseOrderType={'direct'}

                  setModalProductId={setModalProductId}
                  setModalVariantId={setModalVariantId}
                  setModalOptions={setModalOptions}
                  setModalProductName={setModalProductName}
                  setModalType={setModalType}
                  setModalAssignment={setModalAssignment}
                  setShowAddSupplierAssignmentModal={setShowAddSupplierAssignmentModal}
                  activeSupplier={activeSupplier}
                  showSuggestionLevels={false}
                />
              </Box>
            )}
          </Panel>
          

          <Panel>
            <H3>Totals, Adjustments and Dates</H3>
            <div className="flex-container">
              <div className="flex-1">
                <H4>Significant Dates</H4>
                <Datepicker
                  required
                  label="Invoice Date"
                  locale="en-GB"
                  max={nextYear()}
                  min={today()}
                  onDateChange={(value) => setInvoiceD(value)}
                  value={invoiceDate}
                />

                <Datepicker
                  required
                  label="Expected Date"
                  locale="en-GB"
                  max={nextYear()}
                  min={today()}
                  onDateChange={(value) => setExpectedDate(value)}
                  value={expectedDate}
                />

                <Datepicker
                  required
                  label="Payment Due Date"
                  locale="en-GB"
                  max={nextYear()}
                  min={today()}
                  onDateChange={(value) => setPaymentDate(value)}
                  value={paymentDate}
                />
              </div>

              <PurchaseOrderTotals
                purchaseOrderLines={purchaseOrderLines}
                activeSupplier={activeSupplier}
                taxSetting={activeTaxSetting}
                adjustedTotal={adjustedTotal}
                shippingTotal={shippingTotal}
                setAdjustedTotal={setAdjustedTotal}
                setShippingTotal={setShippingTotal}
              />
            </div>
          </Panel>
          

          <Panel>
            <Textarea
              description="Order notes"
              label="Order Notes"
              onChange={(e) => setFieldValue(setNotes, e)}
              placeholder="Notes"
              resize={false}
              rows={3}
              value={notes}
            />
          </Panel>
        

          <Button 
            iconLeft={<AddIcon />}
            onClick={() => handleSubmit()}
            disabled={validPO() || submitted}
          >
            Create Purchase Order
          </Button>
          { !activeOrder?.shipments &&
            <Box
              backgroundColor="warning10"
              border="box"
              borderRadius="normal"
              padding="medium"
            >
              This order has no related shipment information in BigCommerce. This usually means that this is a pickup order, and can't be shipped to a customer.
            </Box>
          }
          { activeSupplier && !activeShippingAddress &&
            <Text color="danger50">Purchase Order Must Specify a Shipping Address</Text>
          }
          { activeSupplier && !activePaymentTerm &&
            <Text color="danger50">Purchase Order Must Specify a Payment Term</Text>
          }
          { activeSupplier && !activePaymentMethod &&
            <Text color="danger50">Purchase Order Must Specify a Payment Method</Text>
          }
          { activeSupplier && !activeTaxSetting &&
            <Text color="danger50">Purchase Order Must Specify a Tax Setting</Text>
          }
          { activeSupplier && !invoiceDate &&
            <Text color="danger50">Purchase Order Must Include an Invoice Date</Text>
          }
          { activeSupplier && !expectedDate &&
            <Text color="danger50">Purchase Order Must Include an Expected Date</Text>
          }
          { activeSupplier && !paymentDate &&
            <Text color="danger50">Purchase Order Must Include an Expected Payment Date</Text>
          }
          { activeSupplier && !(purchaseOrderLines?.lineItems.length > 0) &&
            <Text color="danger50">Purchase Order Must Include Purchase Items</Text>
          }
          { Object.keys(warnings.moq).map(warningId => 
            <>
              <Text color="warning60">{warnings.moq[warningId]}</Text>
            </>
            )
          }
          { Object.keys(warnings.orderMultiple).map(warningId => 
            <>
              <Text color="warning60">{warnings.orderMultiple[warningId]}</Text>
            </>
            )
          }
        </Panel>
      </SettingsPage>

      { showAddSupplierAssignmentModal && 
        <AddSupplierAssignmentModal
          payload={payload}
          showAddSupplierAssignmentModal={showAddSupplierAssignmentModal}
          setShowAddSupplierAssignmentModal={setShowAddSupplierAssignmentModal}
          supplier={activeSupplier}
          modalProductId={modalProductId}
          modalVariantId={modalVariantId}
          modalOptions={modalOptions}
          modalProductName={modalProductName}
          modalType={modalType}
        />
      }
    </>
  )

}


const mapStateToProps = state => ({
  suppliers: state.suppliers,
  newPurchaseOrder: state.purchaseOrders.newPurchaseOrder,
  taxSettings: state.taxSettings,
  paymentTerms: state.paymentTerms,
  locations: state.locations,
  paymentMethods: state.paymentMethods,
  purchaseOrderLines: state.purchaseOrderLines,
  incompleteOrders: state.incompleteOrders,
  activeOrder: state.incompleteOrders.activeOrder,
  products: state.products,
  settings: state.settings,
});

const mapDispatchToProps = (dispatch) => ({
  loadTaxSettings: (payload) => dispatch(taxSettingsIndex(payload)),
  loadPaymentTerms: (payload) => dispatch(paymentTermsIndex(payload)),
  loadPaymentMethods: (payload) => dispatch(paymentMethodsIndex(payload)),
  loadSuppliers: (payload) => dispatch(suppliersIndex(payload)),
  loadLocations: (payload) => dispatch(locationsIndex(payload)),
  addPurchaseOrder: (payload, purchaseOrderJson, purchaseOrderLinesJson) => dispatch(purchaseOrdersCreate(payload, purchaseOrderJson, purchaseOrderLinesJson, 'direct')),
  clearPOLines: () => dispatch(reset()),
  loadOrder: (payload, orderId) => dispatch(orderWithShipments(payload, orderId)),

  quantityUpdate: (payload, itemData, itemKey) => dispatch(purchaseOrderUpdateQuantity(payload, itemData, itemKey)),
  addItem: (payload, itemData) => dispatch(purchaseOrderAddItem(payload, itemData)),
});

export default connect(mapStateToProps, mapDispatchToProps)(NewDropshipPurchaseOrderForm);
